import { UseQueryResult } from '@tanstack/react-query';

import { EVestaAsset, AllVestaAssets, FarmTypes } from '~config/types';
import { GraphSystemVault, GraphUserVault } from '~services/subgraph/vesta/types';
import { IParameters } from '~services/types';

export interface VaultQuery {
  vault: GraphSystemVault;
  computed: ComputedVaultData;
}
export interface ComputedVaultData {
  totalCollateralUsd: number;
  totalDebtUsd: number;
  collateralRatio: number;
}

export type UserVaultComputed = {
  collateralUsd: number;
  debtUsd: number;
  collateralRatio: number;
};

export interface UserVaultQuery {
  vault: GraphUserVault;
  computed: UserVaultComputed;
}

/**
 * Is an extended version of UseQueryResult, with an always defined data field and an invalidate function.
 * @template ReturnType The data response return type. Must extend UseQueryResult.
 */
export interface DeterminedQueryResult<ReturnType> extends Omit<UseQueryResult<ReturnType>, 'data'> {
  data: ReturnType;
  invalidate(): Promise<any>;
}

export type AllParamsQueryResponse = { [index in EVestaAsset]: IParameters };

export interface AllVestaSystemVault {
  vaultInfo: Record<EVestaAsset, GraphSystemVault>;
}

export interface SystemVault {
  vaultInfo: GraphSystemVault;
}

export interface VaultQueryResponse {
  vaultInfo: GraphSystemVault;
  computed: ComputedVaultData;
}
export type AllVaultQueryResponse = { [index in EVestaAsset]: VaultQueryResponse };

export interface ITokenData {
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: number;
}

export type AssetPrices = {
  oracle: { [index in EVestaAsset]: number };
  market: { [index in AllVestaAssets]: number };
};

export interface Allowances {
  [index: string]: string;
}

export interface PayloadAllowance {
  contractAddress: string;
  asset: AllVestaAssets;
  data: Partial<string>;
}

export type ITokens = {
  [index in AllVestaAssets]: ITokenData;
};

export enum ETroveStatusGraph {
  NULL = '',
  ACTIVE = 'open',
  CLOSED_BY_OWNER = 'closedByOwner',
  CLOSED_BY_LIQUIDATION = 'closedByLiquidation',
  CLOSED_BY_REDEMPTION = 'closedByRedemption',
}

export enum ETroveStatusChain {
  NULL = '0',
  ACTIVE = '1',
  CLOSED_BY_OWNER = '2',
  CLOSED_BY_LIQUIDATION = '3',
  CLOSED_BY_REDEMPTION = '4',
}

export interface IUserVault {
  pendingAssetRewards: number;
  pendingUsdvRewards: number;
  collateral: number;
  debt: number;
  stake: number;
  status: ETroveStatusGraph;
  currentICR: number;
  nominalICR: number;
  collateralSurplus: number;
}

export type IUserBalances = {
  [index in AllVestaAssets]: number;
};

export type IUserBalancesRaw = {
  [index in AllVestaAssets]: string;
};

export type UserVaults = {
  [index in EVestaAsset]: IUserVault;
};

export interface IndexedQueriesParams<
  IndexType extends AllVestaAssets | FarmTypes,
  ReturnType,
  ComputedType = any,
> {
  indexValues: IndexType[];
  onArray?: (responses: { data: DeterminedQueryResult<ReturnType>; asset: IndexType }[]) => ComputedType;
  hook: (asset: IndexType) => DeterminedQueryResult<ReturnType>;
}

export type IndexedQueriesReturn<
  IndexType extends string,
  ReturnType,
  ComputedType = any,
> = ComputedType extends undefined
  ? {
      responses: { [index in IndexType]: DeterminedQueryResult<ReturnType> };
      computed?: ComputedType;
    }
  : {
      responses: { [index in IndexType]: DeterminedQueryResult<ReturnType> };
      computed: ComputedType;
    };

export interface PoolComputed {
  totalUserStakesUsd: number;
  totalUserEarnedUsd: number;
  isAnythingLoading: boolean;
  hasAnything: boolean;
}

export interface IntermediatePoolComputed<QueryType, IndexType> {
  data: DeterminedQueryResult<QueryType>;
  asset: IndexType;
}
